import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./button";
import { HiArrowRightOnRectangle } from "react-icons/hi2";
import { MdCancel } from "react-icons/md";
import logo from "../../assets/img/paykit _logo1.webp";
import useAuthStore from "../../stores/authStore";
import { NAV_SECTIONS } from "../../lib/role-config/roleconfig";
import { useGetMerchantFeatureFlags } from "../../hooks/queries/useMerchantQueries";

function NavItem({
  path,
  Icon,
  label,
  isActivePath,
  navButtonClass,
  handleNavigation,
}) {
  return (
    <Button
      variant={isActivePath(path) ? "secondary" : "ghost"}
      className={navButtonClass(path)}
      onClick={() => handleNavigation(path)}
    >
      <Icon className="mr-3 h-4 w-4" />
      {label}
    </Button>
  );
}

function NavSection({
  title,
  items,
  isActivePath,
  navButtonClass,
  handleNavigation,
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 px-1">
        {title}
      </p>
      <nav className="space-y-1">
        {items.map(({ path, Icon, label }) => (
          <NavItem
            key={path}
            path={path}
            Icon={Icon}
            label={label}
            isActivePath={isActivePath}
            navButtonClass={navButtonClass}
            handleNavigation={handleNavigation}
          />
        ))}
      </nav>
    </div>
  );
}

export function Sidebar({ isOpen, onClose }) {
  // Single store subscription — user included here, not in a second call
  const {
    computedUserEmail,
    computedUserName,
    clearAuth,
    computedUser,
    user: tokenUser,
  } = useAuthStore();

  const navigate = useNavigate();
  const location = useLocation();

  const effectiveUser = computedUser || tokenUser;

  const userRoleDisplay = useMemo(() => {
    const userRoles = effectiveUser?.roles || [];
    if (!userRoles || userRoles.length === 0) return "";

    const formattedRoles = userRoles.map((role) => {
      const roleWithoutPrefix = role.replace(/^ROLE_/, "");
      const roleWords = roleWithoutPrefix.split(/[_\s]+/).filter(Boolean);
      return roleWords
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    });

    return formattedRoles.join(" & ");
  }, [effectiveUser?.roles]);

  const { data: featureFlags } = useGetMerchantFeatureFlags();

  const { mainItems, settingsItems } = useMemo(() => {
    const effectiveRoles = effectiveUser?.roles || [];
    const mainMap = new Map();
    const settingsMap = new Map();

    for (const role of effectiveRoles) {
      const sections = NAV_SECTIONS[role];
      if (!sections) continue;
      for (const item of sections.main) mainMap.set(item.path, item);
      for (const item of sections.settings) settingsMap.set(item.path, item);
    }

    const allSettingsItems = Array.from(settingsMap.values());
    const filteredSettingsItems = allSettingsItems.filter((item) => {
      if (item.path === "/developer-apis") return featureFlags?.developer === true;
      return true;
    });

    return {
      mainItems: Array.from(mainMap.values()),
      settingsItems: filteredSettingsItems,
    };
  }, [effectiveUser?.roles, featureFlags]);

  const { userName, userInitials } = useMemo(() => {
    const getInitialsFromEmail = (email) => {
      if (!email) return "Not Found!";
      const emailNameParts = email.split("@")[0].split(".");
      const firstNameInitial = emailNameParts[0]?.[0] ?? "";
      const lastNameInitial = emailNameParts[1]?.[0] ?? "";
      return (firstNameInitial + lastNameInitial).toUpperCase() || "Not Found!";
    };

    return {
      userName: computedUserName || "Guest",
      userInitials: getInitialsFromEmail(computedUserEmail),
    };
  }, [computedUserName, computedUserEmail]);

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    clearAuth();
    navigate("/auth/login");
  };

  const isActivePath = (path) => location.pathname === path;

  const navButtonClass = (path) =>
    `w-full justify-start text-sm font-normal ${
      isActivePath(path)
        ? "bg-primary-600 text-white"
        : "text-gray-300 hover:bg-gray-700 hover:text-white"
    }`;

  const sidebarClasses = `
    fixed inset-y-0 left-0 bg-gray-800 shadow-lg z-[450]
    w-60 h-screen flex flex-col
    transform transition-transform duration-300 ease-in-out
    ${isOpen ? "translate-x-0" : "-translate-x-full"}
    md:translate-x-0
    md:static md:shadow-none md:bg-gray-800 md:border-r md:border-gray-200
  `;

  return (
    <div className={sidebarClasses}>
      <div className="p-6 flex justify-between items-center">
        <div
          className="items-center space-x-2 cursor-pointer hover:opacity-80 flex-grow"
          onClick={() => handleNavigation("/dashboard")}
        >
          <div className="flex flex-row items-center justify-center">
            <img src={logo} alt="paykit logo" className="h-12 w-auto" />
          </div>
        </div>
        <button
          onClick={onClose}
          className="md:hidden text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1"
        >
          <MdCancel className="h-6 w-6 text-blue" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-6">
        {mainItems.length > 0 && (
          <NavSection
            title="Main Menu"
            items={mainItems}
            isActivePath={isActivePath}
            navButtonClass={navButtonClass}
            handleNavigation={handleNavigation}
          />
        )}

        <div>
          <NavSection
            title="Settings"
            items={settingsItems}
            isActivePath={isActivePath}
            navButtonClass={navButtonClass}
            handleNavigation={handleNavigation}
          />
          <Button
            variant="ghost"
            className="w-full justify-start text-sm font-normal text-gray-300 hover:bg-gray-700 hover:text-white mt-1"
            onClick={handleLogout}
          >
            <HiArrowRightOnRectangle className="mr-3 h-4 w-4" />
            Log out
          </Button>
        </div>
      </div>

      <div className="border-t border-gray-700 p-4">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-sm bg-primary-600 flex items-center justify-center flex-shrink-0 border border-white/40 outline outline-1 outline-white/20">
            <span className="text-sm font-semibold text-white">
              {userInitials}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {userName}
            </p>
            <p className="text-xs text-gray-400 truncate">{userRoleDisplay}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
